import {
  LINE_ITEM_ITEM,
  LINE_ITEM_SHIPPING_FEE,
  LINE_ITEM_PICKUP_FEE,
  LISTING_STATE_PUBLISHED,
} from '../../util/types';
import { transitions } from '../../transactions/transactionProcessPurchase';

const DAY_MS = 24 * 60 * 60 * 1000;

export const CANCEL_TRANSITIONS = [
  transitions.CANCEL,
  transitions.AUTO_CANCEL,
  transitions.CANCEL_FROM_DISPUTED,
  transitions.AUTO_CANCEL_FROM_DISPUTED,
];

export const COMPLETE_TRANSITIONS = [
  transitions.MARK_RECEIVED,
  transitions.MARK_RECEIVED_FROM_PURCHASED,
  transitions.MARK_RECEIVED_FROM_DISPUTED,
  transitions.AUTO_MARK_RECEIVED,
  transitions.AUTO_COMPLETE,
];

const FEE_CODES = [LINE_ITEM_SHIPPING_FEE, LINE_ITEM_PICKUP_FEE];

// A sold card's stock drops to 0 but the listing stays published. Listings
// without stock (currentStock not included, or never set) are not sold out,
// matching My Listings' `minStock: 1, stockMode: 'match-undefined'` query.
export const isSoldOut = listing => listing?.currentStock?.attributes?.quantity === 0;

// Not exported from src/util/types.js. Accepted offers are billed as the full
// line-item/item plus a negative line-item/discount (see
// server/api-util/lineItems.js's offerLineMaybe), so the sale amount must
// include the discount to reflect the net price actually paid.
const LINE_ITEM_DISCOUNT = 'line-item/discount';
const SALE_AMOUNT_CODES = [LINE_ITEM_ITEM, LINE_ITEM_DISCOUNT];

export const dayKey = date => date.toISOString().slice(0, 10);

const findTransitionAt = (tx, names) => {
  const found = (tx?.attributes?.transitions || []).find(t => names.includes(t.transition));
  return found ? new Date(found.createdAt) : null;
};

export const getPaidAt = tx => findTransitionAt(tx, [transitions.CONFIRM_PAYMENT]);
export const getCompletedAt = tx => findTransitionAt(tx, COMPLETE_TRANSITIONS);

export const isQualifyingSale = tx =>
  !!getPaidAt(tx) && !CANCEL_TRANSITIONS.includes(tx?.attributes?.lastTransition);

const sumAmounts = items => items.reduce((sum, li) => sum + (li.lineTotal?.amount || 0), 0);

export const getSaleAmount = tx => {
  const lineItems = (tx?.attributes?.lineItems || []).filter(li => !li.reversal);
  const itemLines = lineItems.filter(li => li.code === LINE_ITEM_ITEM);
  const saleLines = lineItems.filter(li => SALE_AMOUNT_CODES.includes(li.code));
  if (itemLines.length > 0) {
    return sumAmounts(saleLines);
  }
  const fees = sumAmounts(lineItems.filter(li => FEE_CODES.includes(li.code)));
  return (tx?.attributes?.payinTotal?.amount || 0) - fees;
};

const getCurrency = tx =>
  tx?.attributes?.payinTotal?.currency ||
  (tx?.attributes?.lineItems || [])[0]?.lineTotal?.currency ||
  null;

const pctChange = (current, previous) =>
  previous > 0 ? Math.round(((current - previous) / previous) * 100) : null;

// Day keys (UTC) for the 7 days ending `endDaysAgo` days before `now`.
const weekKeys = (now, endDaysAgo) =>
  Array.from({ length: 7 }, (_, i) => dayKey(new Date(now.getTime() - (endDaysAgo + i) * DAY_MS)));

const sumViews = (listings, keys) =>
  listings.reduce((sum, l) => {
    const byDay = l.attributes?.publicData?.viewsByDay || {};
    return sum + keys.reduce((s, k) => s + (Number(byDay[k]) || 0), 0);
  }, 0);

const hasTwoWeeksOfViews = (listings, now) => {
  const threshold = dayKey(new Date(now.getTime() - 14 * DAY_MS));
  return listings.some(l =>
    Object.keys(l.attributes?.publicData?.viewsByDay || {}).some(k => k <= threshold)
  );
};

export const computeStats = ({ listings = [], sales = [], now = new Date() }) => {
  const weekAgo = now.getTime() - 7 * DAY_MS;
  const twoWeeksAgo = now.getTime() - 14 * DAY_MS;
  const qualifying = sales.filter(isQualifyingSale);

  const paidBetween = (from, to) =>
    qualifying.filter(tx => {
      const t = getPaidAt(tx).getTime();
      return t >= from && t < to;
    });
  const thisWeek = paidBetween(weekAgo, now.getTime() + 1);
  const lastWeek = paidBetween(twoWeeksAgo, weekAgo);
  const total = txs => txs.reduce((sum, tx) => sum + getSaleAmount(tx), 0);

  // Compares the last 7 COMPLETE days against the 7 days before that -
  // today's partial day is excluded from both windows so the trend isn't
  // biased negative by an in-progress day.
  const viewsThisWeek = sumViews(listings, weekKeys(now, 1));
  const viewsLastWeek = sumViews(listings, weekKeys(now, 8));

  return {
    totalListings: listings.filter(
      l => l.attributes?.state === LISTING_STATE_PUBLISHED && !isSoldOut(l)
    ).length,
    newListings7d: listings.filter(
      l =>
        l.attributes?.state === LISTING_STATE_PUBLISHED &&
        !isSoldOut(l) &&
        new Date(l.attributes?.createdAt).getTime() >= weekAgo
    ).length,
    itemsSold: qualifying.length,
    itemsSold7d: thisWeek.length,
    totalSales: total(qualifying),
    salesTrendPct: pctChange(total(thisWeek), total(lastWeek)),
    totalViews: listings.reduce(
      (sum, l) => sum + (Number(l.attributes?.publicData?.totalViews) || 0),
      0
    ),
    viewsTrendPct: hasTwoWeeksOfViews(listings, now)
      ? pctChange(viewsThisWeek, viewsLastWeek)
      : null,
    currency: qualifying.length > 0 ? getCurrency(qualifying[0]) : null,
  };
};

export const buildSalesSeries = (sales = [], rangeDays = 30, now = new Date()) => {
  const amountsByDay = sales.filter(isQualifyingSale).reduce((acc, tx) => {
    const key = dayKey(getPaidAt(tx));
    return { ...acc, [key]: (acc[key] || 0) + getSaleAmount(tx) };
  }, {});

  let running = 0;
  return Array.from({ length: rangeDays }, (_, i) => {
    const date = new Date(now.getTime() - (rangeDays - 1 - i) * DAY_MS);
    const key = dayKey(date);
    running += amountsByDay[key] || 0;
    return { date: new Date(`${key}T00:00:00.000Z`), total: running };
  });
};

const niceStep = rawStep => {
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalized = rawStep / magnitude;
  const nice = [1, 2, 2.5, 5, 10].find(n => normalized <= n);
  return nice * magnitude;
};

export const niceTicks = (maxValue, count = 5) => {
  const max = maxValue > 0 ? maxValue : 1000;
  const step = niceStep(max / (count - 1));
  return Array.from({ length: count }, (_, i) => i * step);
};

export const formatAxisMoney = dollars => {
  if (dollars >= 1000) {
    const k = Math.round((dollars / 1000) * 10) / 10;
    return `$${k}k`;
  }
  return `$${Math.round(dollars)}`;
};

const enumLabel = (listingFields, key, value) => {
  if (!value) return null;
  const field = (listingFields || []).find(f => f.key === key);
  const label = field?.enumOptions?.find(o => o.option === value)?.label;
  return label ? label.trim() : null;
};

export const getGradeLine = (publicData, listingFields) => {
  const data = publicData || {};
  const company = enumLabel(listingFields, 'gradingcompany', data.gradingcompany);
  const grade = enumLabel(listingFields, 'gradeofcard', data.gradeofcard);
  if (company || grade) {
    return [company, grade].filter(Boolean).join(' ');
  }
  return enumLabel(listingFields, 'condition', data.condition);
};

// An offer chat message ("Offered: $X") is posted with the metadata update;
// a message this close to an offer item is that same event, not a new one.
const OFFER_MESSAGE_WINDOW_MS = 5 * 60 * 1000;

const displayName = user => user?.attributes?.profile?.displayName || '';

// Offer prices are dollars in transaction metadata (OfferButtons.js). The
// buyer side is customerOfferPrice/timeOfCustomerOffer, the seller side
// offerPrice/timeOfOffer. A cancelled or declined side is 0/null. Equal prices
// mean the offer was accepted. Only the newest offer event per tx is shown.
const offerItem = ({ tx, isSale }) => {
  const m = tx.attributes?.metadata || {};
  const sides = [
    { byProvider: false, price: m.customerOfferPrice, at: m.timeOfCustomerOffer },
    { byProvider: true, price: m.offerPrice, at: m.timeOfOffer },
  ].filter(side => side.price > 0 && side.at);
  if (sides.length === 0) return null;

  const latest = sides.reduce((a, b) => (b.at > a.at ? b : a));
  const isAccepted = m.customerOfferPrice > 0 && m.customerOfferPrice === m.offerPrice;
  const sentByMe = latest.byProvider === isSale;
  const type = isAccepted ? 'offerAccepted' : sentByMe ? 'offerSent' : 'offerReceived';
  const txId = tx.id.uuid;
  return {
    key: `offer-${txId}-${latest.at}`,
    type,
    at: new Date(latest.at),
    txId,
    isSale,
    listingTitle: tx.listing?.attributes?.title || '',
    amount: Math.round(latest.price * 100),
    currency: 'USD',
    otherName: displayName(isSale ? tx.customer : tx.provider),
  };
};

export const buildActivity = ({
  sales = [],
  messages = [],
  listings = [],
  offers = [],
  limit = 4,
}) => {
  const saleItems = sales.filter(isQualifyingSale).flatMap(tx => {
    const txId = tx.id.uuid;
    const base = {
      txId,
      isSale: true,
      listingTitle: tx.listing?.attributes?.title || '',
      amount: getSaleAmount(tx),
      currency: getCurrency(tx),
    };
    const completedAt = getCompletedAt(tx);
    const sold = { ...base, key: `sold-${txId}`, type: 'sold', at: getPaidAt(tx) };
    return completedAt
      ? [sold, { ...base, key: `complete-${txId}`, type: 'complete', at: completedAt }]
      : [sold];
  });

  const offerItems = offers.map(offerItem).filter(Boolean);
  const isOfferMessage = m =>
    offerItems.some(
      o => o.txId === m.txId && Math.abs(new Date(m.createdAt) - o.at) <= OFFER_MESSAGE_WINDOW_MS
    );

  const messageItems = messages
    .filter(m => !isOfferMessage(m))
    .map(m => ({
      key: `message-${m.txId}-${m.createdAt}`,
      type: 'message',
      at: new Date(m.createdAt),
      txId: m.txId,
      isSale: m.isSale,
      senderName: m.senderName,
      listingTitle: m.listingTitle,
    }));

  const watchItems = listings.flatMap(l =>
    (l.attributes?.publicData?.recentWatches || []).map(at => ({
      key: `watch-${l.id.uuid}-${at}`,
      type: 'watch',
      at: new Date(at),
      listingId: l.id.uuid,
      listingTitle: l.attributes?.title || '',
    }))
  );

  return [...saleItems, ...offerItems, ...messageItems, ...watchItems]
    .filter(item => item.at && !Number.isNaN(item.at.getTime()))
    .sort((a, b) => b.at - a.at)
    .slice(0, limit);
};
